import { computed, onMounted, watch } from 'vue'

import { recommendationsAPI } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useAuthStore } from '@backmarket/nuxt-module-oauth/useAuthStore'

import type { Product, Specifications } from '~/scopes/core/models/product'

import {
  getUserRecommendationId,
  setRecommendationDebugExtension,
} from '../recommendationHelper'

function getWarrantyDuration(warranty: string): number | undefined {
  try {
    return parseInt(warranty, 10)
  } catch (err) {
    return undefined
  }
}

function toProduct(product: recommendationsAPI.Product): Product {
  const result = {
    id: product.uuid,
    title: product.displayTitle || product.title,
    description: product.subTitles?.join(' - '),
    image: product.images[0],
    grade: product.listing.grade,
    productPageLink: product.link,
    price: product.listing.price,
    priceNew: product.referencePrice,
    reviewRating: product.reviewRating,
    brand: product.brand,
    category: product.category,
    listingId: product.listing.id,
    legacyId: product.id,
    sellerId: product.listing.sellerId,
    model: product.displayTitle ?? 'unknown',
    specifications: product.specifications.reduce(
      (specs, spec) => ({
        ...specs,
        [spec.field]: spec.values[0].label,
      }),
      {} as Specifications,
    ),
    availableStock: product.listing.stockWarning,
    warrantyDuration: getWarrantyDuration(product.listing.warranty),
  }

  return result
}

function useIdentity() {
  const { userId } = useAuthStore()
  if (userId)
    return {
      identityRepository: 'customerId',
      identity: userId,
    }
  const attraqtSessionId = getUserRecommendationId()
  if (attraqtSessionId)
    return {
      identityRepository: 'sessionId',
      identity: attraqtSessionId,
    }

  return {
    identityRepository: undefined,
    identity: undefined,
  }
}

export function useRecommendedProducts({
  limit = 4,
  scope,
  scopeId,
  category,
  widgetId,
  personalisation,
}: {
  limit?: number
  scope?: string
  scopeId?: string
  category?: string
  widgetId?: string
  personalisation?: boolean
}) {
  const { identity, identityRepository } = personalisation
    ? useIdentity()
    : {
        identity: undefined,
        identityRepository: undefined,
      }

  const { data, execute, pending, error } = widgetId
    ? useHttpFetch(recommendationsAPI.getRecommendationById, {
        queryParams: {
          limit,
          identity,
          identityRepository,
        },
        pathParams: { widgetId },
        immediate: false,
      })
    : useHttpFetch(recommendationsAPI.getAllRecommendations, {
        queryParams: {
          limit,
          scope,
          scopeId,
          category,
          identityRepository,
          identity,
        },
        immediate: false,
      })

  onMounted(() => {
    void execute()
  })

  watch(data, () => {
    const payload = data.value
    if (payload) {
      const result = Array.isArray(payload) ? payload[0] : payload
      setRecommendationDebugExtension(result.recommendationRequestId)
    }
  })

  const products = computed(() => {
    if (!data.value) return []

    const results = Array.isArray(data.value) ? data.value[0] : data.value

    return results.products.map((product) => toProduct(product))
  })

  return { pending, products, error }
}

export function useBundleProduct(mainProduct: Product) {
  const { execute, pending, data } = useHttpFetch(
    recommendationsAPI.getAllRecommendations,
    {
      queryParams: {
        limit: 1,
        scope: 'listingBundle',
        scopeId: mainProduct.listingId,
        category: 'bundles',
      },
      lazy: true,
      default: () => ({ products: [], widgetId: '' }),
      transform: (response) => response?.[0] ?? null,
      immediate: false,
    },
  )

  const recommendedProduct = computed<Product | null>(() =>
    data.value.products.length > 0 ? toProduct(data.value.products[0]) : null,
  )

  const totalPrice = computed(() =>
    recommendedProduct.value
      ? parseFloat(mainProduct.price.amount) +
        parseFloat(recommendedProduct.value.price.amount)
      : null,
  )

  const widgetId = computed(() => data.value.widgetId)

  onMounted(() => {
    void execute()
  })

  return {
    pending,
    recommendedProduct,
    totalPrice,
    widgetId,
  }
}
